import React, {useState, useEffect} from 'react';
import {useAuth} from '../hooks/useAuth';
import {apiClient} from '../services';
import {useAppSelector, useAppDispatch} from '../hooks/redux';
import {selectTests, fetchTests} from '../slices/testSlice';
import {selectCurrentPatient, selectSubPatients, restoreCurrentPatientFromStorage} from '../slices/patientSlice';
import {useDashboardData} from '../hooks/useDashboardData';
import {Recommendation} from '../types/api';
import {getCategoryStats} from '../utils/healthCategories';
import DashboardLayout from '../components/layout/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import HealthOverview from '../components/dashboard/HealthOverview';
import MyLabsPlusCard from '../components/dashboard/MyLabsPlusCard';
import LabTrends from '../components/dashboard/LabTrends';
import LabResults from '../components/dashboard/LabResults';
import MarkersOverview from '../components/dashboard/MarkersOverview';
import Recommendations from '../components/dashboard/Recommendations';
// import WellnessCoachingCard from '../components/dashboard/WellnessCoachingCard';
import MembershipLock from '../components/common/MembershipLock';
import LoadingSpinner from '../components/common/LoadingSpinner';

const Dashboard: React.FC = () => {
    const {user} = useAuth();
    const dispatch = useAppDispatch();
    const testsState = useAppSelector(selectTests);
    const currentPatient = useAppSelector(selectCurrentPatient);
    const subPatients = useAppSelector(selectSubPatients);

    // Use dashboard-specific data (dummy data if not subscribed)
    const {data: currentPatientData, isSubscribed} = useDashboardData();

    // Track selected health category
    const [selectedCategory, setSelectedCategory] = useState<string>('heart');
    const [hasInitializedCategory, setHasInitializedCategory] = useState<boolean>(false);

    // Track selected biomarker for synchronization between LabResults and LabTrends
    const [selectedBiomarkerName, setSelectedBiomarkerName] = useState<string | null>(null);

    // Update selected category to first available category with data when data loads (only once)
    useEffect(() => {
        if (currentPatientData.length > 0 && !hasInitializedCategory) {
            const allResults = currentPatientData.flatMap(
                (test: any) =>
                    test.results?.map((result: any) => ({
                        ...result,
                        createdDate: test.createdDate,
                    })) || []
            );

            const categoryStats = getCategoryStats(allResults);

            // Filter and sort categories the same way as HorizontalCategoryChart to get the true "first" category
            const availableCategories = categoryStats
                .filter((category) => category.availableTests > 0)
                .sort((a, b) => b.averageValue - a.averageValue); // Sort by score descending (same as chart)

            const firstAvailableCategory = availableCategories[0];

            if (firstAvailableCategory) {
                setSelectedCategory(firstAvailableCategory.key);
                setHasInitializedCategory(true);
            }
        }
    }, [currentPatientData, hasInitializedCategory]);

    // Track if we've attempted to fetch data to prevent loops with empty responses
    const [hasFetchedData, setHasFetchedData] = React.useState(false);

    // Ensure API client has the latest token before making requests
    React.useEffect(() => {
        const initializeToken = async () => {
            if (user?.token) {
                apiClient.setToken(user.token);
            } else {
                // Try to refresh from localStorage
                apiClient.refreshTokenFromStorage();
            }
        };

        initializeToken();
    }, [user?.token]);

    // Initialize data when component mounts and user is authenticated - using same logic as Results page
    React.useEffect(() => {
        const shouldFetchTests =
            user?.isLoggedIn &&
            !testsState.loading &&
            !hasFetchedData && // Prevent infinite loops with empty responses
            (testsState.tests.length === 0 || // No tests loaded yet
                (subPatients.length === 0 && testsState.tests.length > 0) || // Have tests but no patients (possible after token refresh)
                ((!currentPatient?.id || currentPatient.id === 0) && subPatients.length === 0)); // No valid current patient and no sub patients

        if (shouldFetchTests) {
            setHasFetchedData(true);
            dispatch(fetchTests());
        }
    }, [
        dispatch,
        user?.isLoggedIn,
        testsState.tests.length,
        testsState.loading,
        subPatients.length,
        currentPatient?.id,
        hasFetchedData,
    ]);

    // Reset fetch flag when user changes (e.g., during impersonation)
    React.useEffect(() => {
        setHasFetchedData(false);
    }, [user?.userInfo?.id]);

    // Restore current patient from localStorage on page load (handles token refresh scenarios)
    React.useEffect(() => {
        if ((!currentPatient?.id || currentPatient.id === 0) && user?.token) {
            dispatch(restoreCurrentPatientFromStorage());
        }
    }, [dispatch, currentPatient?.id, user?.token]);

    // Error handling (Redux doesn't currently track errors in testSlice)
    const error = null;

    // Get current hour for time-based greeting
    const currentHour = new Date().getHours();
    const getGreeting = () => {
        if (currentHour < 12) return 'Good Morning';
        if (currentHour < 17) return 'Good Afternoon';
        return 'Good Evening';
    };

    // Get user's first name from current patient (set during login) or auth user
    // Normalize to capitalize only first letter, rest lowercase
    const rawUserName = currentPatient?.firstName || user?.userInfo?.firstName || 'User';
    const userName = rawUserName.charAt(0).toUpperCase() + rawUserName.slice(1).toLowerCase();
    const greeting = `${getGreeting()}, ${userName}`;

    // Check if user is a member - use API subscription status
    const isMember = isSubscribed;

    // Extract all unique recommendations from test data
    const allRecommendations = React.useMemo(() => {
        const recommendationsMap = new Map<number, Recommendation>();

        currentPatientData.forEach((test: any) => {
            if (test.recommendations && Array.isArray(test.recommendations)) {
                test.recommendations.forEach((rec: Recommendation) => {
                    if (rec.enabled) {
                        recommendationsMap.set(rec.Id, rec);
                    }
                });
            }
        });

        return Array.from(recommendationsMap.values());
    }, [currentPatientData]);

    // Show loading spinner while data is being fetched
    if (testsState.loading) {
        return (
            <DashboardLayout isMember={isMember}>
                <LoadingSpinner />
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout isMember={isMember}>
            <div className="relative">
                <div className={!isMember ? 'pointer-events-none select-none filter blur-sm' : ''}>
                    <PageHeader title={greeting} />

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                            <p className="text-red-600">
                                Unable to load your health data. Please try refreshing the page.
                            </p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
                        <div className="lg:col-span-3 space-y-6">
                            {/* Mobile: Show all components in desired order */}
                            <div className="lg:hidden space-y-6">
                                <HealthOverview
                                    selectedCategory={selectedCategory}
                                    onCategoryChange={setSelectedCategory}
                                    onBiomarkerSelect={setSelectedBiomarkerName}
                                    selectedBiomarkerName={selectedBiomarkerName}
                                />
                                <LabResults
                                    selectedBiomarkerName={selectedBiomarkerName}
                                    onBiomarkerChange={setSelectedBiomarkerName}
                                    onCategoryChange={setSelectedCategory}
                                />
                                <LabTrends
                                    selectedCategory={selectedCategory}
                                    onTrendSelect={setSelectedBiomarkerName}
                                />
                                <MyLabsPlusCard isSubscribed={isSubscribed} />
                                <Recommendations
                                    recommendations={allRecommendations}
                                    loading={testsState.loading}
                                    isSubscribed={isSubscribed}
                                />
                                {/* <WellnessCoachingCard /> */}
                            </div>

                            {/* Desktop: Show main content only */}
                            <div className="hidden lg:block space-y-6">
                                <HealthOverview
                                    selectedCategory={selectedCategory}
                                    onCategoryChange={setSelectedCategory}
                                    onBiomarkerSelect={setSelectedBiomarkerName}
                                    selectedBiomarkerName={selectedBiomarkerName}
                                />
                                <LabResults
                                    selectedBiomarkerName={selectedBiomarkerName}
                                    onBiomarkerChange={setSelectedBiomarkerName}
                                    onCategoryChange={setSelectedCategory}
                                />
                                <LabTrends
                                    selectedCategory={selectedCategory}
                                    onTrendSelect={setSelectedBiomarkerName}
                                />
                                <Recommendations
                                    recommendations={allRecommendations}
                                    loading={testsState.loading}
                                    isSubscribed={isSubscribed}
                                />
                            </div>
                        </div>

                        {/* Desktop: Show sidebar */}
                        <div className="hidden lg:block space-y-6">
                            <MyLabsPlusCard isSubscribed={isSubscribed} />
                            <MarkersOverview />
                            {/* <WellnessCoachingCard /> */}
                        </div>
                    </div>
                </div>
            </div>

            {/* Membership lock overlay for non-members - outside blurred content */}
            {!isMember && <MembershipLock />}
        </DashboardLayout>
    );
};

export default Dashboard;
